module Hereabout.UI.Components.EventComponent where

import Prelude

import Control.Comonad.Env (asks)
import Data.Array as A
import Data.DateTime (DateTime(..), exactDate, Month(..))
import Data.Enum (toEnum)
import Data.Formatter.DateTime as DT
import Data.List as L
import Data.Maybe (Maybe(..), fromJust)
import Data.Maybe as M
import DateTime.Format (formatDateTimeLocally)
import Effect.Aff.Class (class MonadAff)
import Effect.Class (class MonadEffect, liftEffect)
import Halogen (ClassName(..), RefLabel(..))
import Halogen as H
import Halogen.HTML as HH
import Halogen.HTML.Events as HE
import Halogen.HTML.Properties as HP
import Hereabout.Data.Route (Route(..), routeCodec)
import Hereabout.Effects (class Favourite, class Navigate, class SharePost, class UserSession, currentUser, favourite, navigate, unfavourite)
import Hereabout.Types (Comment(..), Event(..), EventType(..), EventWithComments(..), Place(..), User(..), Venue(..))
import Hereabout.UI.Bits (renderIcon, renderIconWithTitle)
import Hereabout.UI.Bulma.Classes as Bulma
import Hereabout.UI.Calendar (Calendar, createEventCalendar)
import Hereabout.UI.Components.CommentComponent as CommentComp
import Hereabout.UI.Components.ContentComponent as ContentComp
import Hereabout.UI.Components.PostCommentComponent as PostCommentComp
import Hereabout.UI.SemanticUI.Classes as SemUI
import Partial.Unsafe (unsafePartial)
import Routing.Duplex (print)
import Type.Proxy (Proxy(..))
import Web.Event.Event (preventDefault)
import Web.Event.Internal.Types as WE
import Web.UIEvent.MouseEvent as ME


data Action
  = InitComponent
  | NavigateToRoute WE.Event Route
  | RaiseSignInRequest
  | FavouriteEvent
  | UnFavouriteEvent

data Output
  = RequestSignIn

type State =
  { eventWithComments :: (EventWithComments String)
  , currentUser :: Maybe User
  , calendar :: Maybe Calendar
  }

type Slot id = forall q. H.Slot q Output id

type ChildSlots =
  ( commentComp :: CommentComp.Slot String
  , postCommentComp :: PostCommentComp.Slot Unit
  , contentComp :: ContentComp.Slot Unit
  )

component :: forall q m. MonadAff m => MonadEffect m => Navigate m => UserSession m => SharePost m => Favourite m => H.Component q (EventWithComments String) Output m
component =
  H.mkComponent
    { initialState: \s -> { eventWithComments : s, currentUser : Nothing, calendar : Nothing }
    , render
    , eval: H.mkEval H.defaultEval { handleAction = handleAction, initialize = Just InitComponent }
    }

formatMinutes :: Int -> String
formatMinutes 1 = 
  "1 minute"
formatMinutes 60 = 
  "1 hour"
formatMinutes 61 = 
  "1 hour 1 minute"
formatMinutes x | x `mod` 60 == 0 && x > 0 = 
  show (x `div` 60)  <> " hours"
formatMinutes x | x > 60 && x `mod` 60 == 1 = 
  show (x `div` 60)  <> " hours 1 minute"
formatMinutes x | x > 60 = 
  show (x `div` 60)  <> " hours " <> show (x `mod` 60) <> " minutes"
formatMinutes x = 
  show x  <> " minutes"

dateTimeFormatter :: DT.Formatter
dateTimeFormatter = L.fromFoldable [DT.DayOfMonth, DT.Placeholder " ", DT.MonthFull, DT.Placeholder " ", DT.YearFull, DT.Placeholder " @ ", DT.Hours24, DT.Placeholder ":", DT.MinutesTwoDigits]

renderPlace :: ∀ (s ∷ Type). Place -> HH.HTML s Action
renderPlace (GPSPosPlace gps) = 
  renderIcon "fa-location-crosshairs" $ HH.a [ ] [HH.text "Location"]
renderPlace (VenuePlace (Venue venue)) = 
  renderIcon "fa-building" $ HH.a [ HP.href (print routeCodec (VenueRoute venue.id)), HE.onClick \me -> NavigateToRoute (ME.toEvent me) (VenueRoute venue.id) ] [HH.text venue.name]


renderEventType ∷ forall cs m. EventType -> H.ComponentHTML Action cs m
renderEventType (EventType eventType) =
  HH.div [ HP.classes [ Bulma.tags ] ]
    [ HH.span [ HP.classes [ Bulma.tag ] ]
      [ case eventType.icon of
          Just icon ->
            HH.span [ HP.classes [ Bulma.iconText ] ]
              [ HH.span [ HP.classes [ Bulma.icon] ]
                [ HH.i [ HP.classes [ Bulma.fas, ClassName icon ] ] []
                ]
              , HH.span_ 
                [ HH.text eventType.name
                ]
              ]
          Nothing -> HH.text eventType.name
      ]
    ]

calendarRef :: RefLabel
calendarRef = RefLabel "calendar-ref"

render :: forall m. MonadAff m => MonadEffect m => Navigate m => SharePost m => State -> H.ComponentHTML Action ChildSlots m
render s =
  HH.div_ $
    [ HH.div [ HP.classes [Bulma.block] ] $
        maybeImage
        <>  [ HH.h1 [ HP.classes [ Bulma.title ] ] [ HH.text event.name ]
            , HH.p_ 
              [ renderIcon "fa-calendar" $ HH.text $ formatDateTimeLocally event.timestamp
              ]
            , HH.p_ 
              [ renderIcon "fa-hourglass" $ HH.text $ formatMinutes (event.duration `div` 60)
              ]
            , HH.p_
              [ renderPlace (event.place)
              ]
            ] 
              <> M.maybe [] (\signupUrl -> [ HH.p_ [ renderIcon "fa-users" $ HH.a [ HP.href signupUrl ] [ HH.text "Register" ] ] ]) event.signupUrl
              <>  [ HH.p [ HP.classes [ Bulma.mt3, Bulma.mb3 ] ] $
                    [ M.maybe (HH.div_ []) renderEventType event.type
                    ]
                  , HH.div [ HP.classes [ Bulma.block ] ] 
                    [ HH.slot_ (Proxy :: _ "contentComp") unit ContentComp.component event.content
                    ]
                  , HH.nav [ HP.classes [ Bulma.level, Bulma.isMobile ] ]
                    [ HH.div [ HP.classes [ Bulma.levelLeft ] ]
                      [ case s.currentUser of
                          Just _ | event.isFavourite == Just true  ->  
                            HH.a [ HE.onClick $ \_ -> UnFavouriteEvent, HP.classes [ Bulma.hasTextWarning ] ] 
                              [ renderIconWithTitle "fa-star" "Favourites" $ HH.text (show event.favouriteCount)
                              ]
                          Just _ ->  
                            HH.a [ HE.onClick $ \_ -> FavouriteEvent ] 
                              [ renderIconWithTitle "fa-star" "Favourites" $ HH.text (show event.favouriteCount)
                              ]
                          Nothing -> 
                            renderIconWithTitle "fa-star" "Favourites" $ HH.text (show event.favouriteCount)
                      ]
                    ]
                  ]
    , HH.div [ HP.classes [Bulma.block] ] $
      [ HH.h1 [ HP.classes [ Bulma.title ] ]
        [ HH.text "Event Timetable" ]
      , HH.div [ HP.ref calendarRef ]
        []
      ]
    , HH.div [ HP.classes [Bulma.block] ] $
      [ HH.h1 [ HP.classes [ Bulma.title ] ]
        [ HH.text "Comments" ]
      , case s.currentUser of
          Just user -> 
            HH.slot_ (Proxy :: _ "postCommentComp") unit PostCommentComp.component { user : user, targetId : event.id}
          Nothing -> 
            HH.div [ HP.classes [ Bulma.box, Bulma.mb1 ] ]
              [ HH.text "Please "
              , HH.a [ HE.onClick $ \_ -> RaiseSignInRequest ] [ HH.text "Sign In" ]
              , HH.text " to share your thoughts."

              ]
      ] <> (map (\(Comment emsg) -> HH.slot_ (Proxy :: _ "commentComp") emsg.id CommentComp.component (Comment emsg) ) $ comments)
    ]
  where
    (EventWithComments event) = s.eventWithComments
    comments = event.comments
    maybeImage =
      case event.image of
        Just imSrc -> 
          [ HH.div [ HP.classes [ Bulma.cardImage, Bulma.hasTextCentred ] ]
            [ HH.figure [ HP.classes [ Bulma.image, Bulma.isInlineBlock ] ] 
              [ HH.img [ HP.src imSrc, HP.style "max-height:700px" ]
              ]
            ]
          ]
        Nothing -> []

handleAction :: forall cs m. MonadEffect m => Navigate m => UserSession m => Favourite m => Action -> H.HalogenM State Action cs Output m Unit
handleAction InitComponent = do
  currentUser' <- H.lift currentUser
  ewc <- H.gets (\s -> s.eventWithComments)
  mcalendarRef <- H.getHTMLElementRef $ calendarRef
  let calendarRef' = unsafePartial $ fromJust mcalendarRef
  calendar <- liftEffect $ createEventCalendar  calendarRef' ewc
  H.modify_ _ { currentUser = currentUser', calendar = Just calendar }
handleAction (NavigateToRoute e route) = do
  H.liftEffect $ preventDefault e
  H.lift $ navigate route
handleAction (RaiseSignInRequest) = do
  H.raise RequestSignIn
handleAction (FavouriteEvent) = do
  (EventWithComments ewc) <- H.gets (\s -> s.eventWithComments)
  H.modify_ _ { eventWithComments = EventWithComments $ ewc { favouriteCount = ewc.favouriteCount + 1, isFavourite = Just true } }
  H.lift $ favourite ewc.id
handleAction (UnFavouriteEvent) = do
  (EventWithComments ewc) <- H.gets (\s -> s.eventWithComments)
  H.modify_ _ { eventWithComments = EventWithComments $ ewc { favouriteCount = ewc.favouriteCount - 1, isFavourite = Just false } }
  H.lift $ unfavourite ewc.id
  
